import { Accordion, AccordionSummary, AccordionDetails } from "@wealthx/ui";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import classNames from "classnames";
import { useCallback, useEffect, useState, useMemo } from "react";
import { useIsTablet } from "../../../hooks/common.hooks";
import { useActiveMenuItem } from "../../../hooks/menuItems.hook";
import { useTenantStatus } from "../../../hooks/tenant.hooks.ts";
import { getCurrentPath } from "../../../utils/get-params-value";
import { primaryColor } from "../../../scss/colors.module.scss";
import { Logout } from "../../../svgs";
import { logout } from "../../../utils/auth";
import SideBarItem from "./side-bar-item";
import CollapsibleMenuItem from "./collapsible-menu-item";
import CollapsibleMenu from "./collapsible-menu";
import "./desktop-sidebar.scss";

const DesktopSideBar = ({ name: fullName = "", gitVersion = {}, sideBarMenuItem = [] }) => {
  const isTablet = useIsTablet();
  const { agencyID } = useTenantStatus();
  const [logoutExpanded, setLogoutExpanded] = useState(false);
  const [sidebarCollapse, setSidebarCollapse] = useState(true);
  const [activeConversationID, setActiveConversationID] = useState(null);
  const { gitDate } = gitVersion;
  const activeTabIndex = useActiveMenuItem(sideBarMenuItem);
  const pathName = getCurrentPath();

  // Check if Conversations is active to show expanded menu
  const isConversationsActive = useMemo(() => {
    return pathName.includes("ai-builder/conversations");
  }, [pathName]);

  const shouldShowMenu = isConversationsActive;

  useEffect(() => {
    const timer = setTimeout(() => {
      setLogoutExpanded(false);
    }, 0);
    return () => clearTimeout(timer);
  }, [isTablet]);

  // Track active conversation from chat page events
  useEffect(() => {
    const handleResume = (e) => {
      setActiveConversationID(e.detail?.conversationID ?? null);
    };
    const handleNewChat = () => {
      setActiveConversationID(null);
    };
    const handleConversationChanged = (e) => {
      setActiveConversationID(e.detail?.conversationID ?? null);
    };
    window.addEventListener("backoffice-resume-chat", handleResume);
    window.addEventListener("backoffice-new-chat", handleNewChat);
    window.addEventListener("backoffice-conversation-changed", handleConversationChanged);
    return () => {
      window.removeEventListener("backoffice-resume-chat", handleResume);
      window.removeEventListener("backoffice-new-chat", handleNewChat);
      window.removeEventListener("backoffice-conversation-changed", handleConversationChanged);
    };
  }, []);

  const handleDeleteActiveConversation = useCallback(() => {
    setActiveConversationID(null);
    window.dispatchEvent(new CustomEvent("backoffice-new-chat"));
  }, []);

  const handleLogoutExpanded = () => {
    setLogoutExpanded(!logoutExpanded);
  };
  const handleToggleSidebar = (value) => {
    if (!value) {
      setLogoutExpanded(false);
    }
    setSidebarCollapse(!value);
  };
  const handleLogout = () => {
    logout();
  };

  return (
    <>
      <div
        onMouseLeave={() => handleToggleSidebar(false)}
        onMouseEnter={() => handleToggleSidebar(true)}
        className={classNames("desktop-side-bar", {
          collapsed: sidebarCollapse,
        })}
      >
        <div className="desktop-side-bar-container">
          <Accordion
            className="position-relative"
            square
            expanded={logoutExpanded}
            onChange={!sidebarCollapse ? handleLogoutExpanded : undefined}
            classes={{
              root: "desktop-sidebar-accordion-root",
              expanded: "desktop-sidebar-accordion-expanded",
            }}
          >
            <AccordionSummary
              aria-controls="panel3d-content"
              id="panel3d-header"
              classes={{
                root: "desktop-sidebar-summary-root",
                content: "desktop-sidebar-summary-content",
                expanded: "desktop-sidebar-summary-expanded",
              }}
            >
              <div className="side-bar-user">
                <div className="side-bar-user__name">
                  {sidebarCollapse ? fullName.match(/\b(\w)/g) : fullName}
                </div>
                <ExpandMoreIcon />
              </div>
            </AccordionSummary>
            <AccordionDetails
              className="logout-button-group"
              classes={{
                root: "desktop-sidebar-details-root",
              }}
            >
              <div className="w-100">
                <div className="w-100" onClick={handleLogout}>
                  <SideBarItem
                    icon={<Logout fillcolor={primaryColor} borderColor="#000" />}
                    title="Logout"
                    checked={false}
                    href=""
                  />
                </div>
                <div className="code-version">
                  <p className="m-0">
                    Last Updated On: {new Date(gitDate).toLocaleString() || " "}
                  </p>
                </div>
              </div>
            </AccordionDetails>
          </Accordion>
          <div className="side-bar-item-group">
            <div className="side-bar-item-group__container">
              {sideBarMenuItem.map((sideBarItem, index) => {
                if (sideBarItem.isCollapsible && sideBarItem.subItems) {
                  // Mark sub-items as active based on current path
                  const subItemsWithActive = sideBarItem.subItems.map((subItem) => ({
                    ...subItem,
                    isActive: pathName.includes(subItem.href),
                  }));

                  return (
                    <CollapsibleMenuItem
                      key={sideBarItem.title}
                      icon={sideBarItem.icon}
                      title={sideBarItem.title}
                      href={sideBarItem.href}
                      subItems={subItemsWithActive}
                      isActive={subItemsWithActive.some((subItem) => subItem.isActive)}
                      sidebarCollapse={sidebarCollapse}
                    />
                  );
                }

                return (
                  <SideBarItem
                    key={sideBarItem.title}
                    icon={sideBarItem.icon}
                    title={sideBarItem.title}
                    href={sideBarItem.href}
                    isActive={index === activeTabIndex}
                  />
                );
              })}
            </div>
          </div>
        </div>
      </div>
      {isConversationsActive && (
        <CollapsibleMenu
          isVisible={shouldShowMenu}
          sidebarCollapse={sidebarCollapse}
          activeConversationID={activeConversationID}
          agencyId={agencyID}
          onDeleteActiveConversation={handleDeleteActiveConversation}
        />
      )}
    </>
  );
};

DesktopSideBar.propTypes = {};

export default DesktopSideBar;
